import { useState, useEffect, useCallback } from 'react'

export type TimeRange =
  | { preset: '1d' | '7d' | '30d' | '90d' | '365d' }
  | { preset: 'custom'; start: string; end: string }
  | { preset: 'all' }

const PRESETS: { value: TimeRange; label: string }[] = [
  { value: { preset: '1d' }, label: '24h' },
  { value: { preset: '7d' }, label: '7d' },
  { value: { preset: '30d' }, label: '30d' },
  { value: { preset: '90d' }, label: '90d' },
  { value: { preset: '365d' }, label: '1y' },
  { value: { preset: 'all' }, label: 'All' },
]

// Allora block time is ~5 seconds
const BLOCK_TIME_SECONDS = 5
const BLOCKS_PER_HOUR = 3600 / BLOCK_TIME_SECONDS // 720
const BLOCKS_PER_DAY = BLOCKS_PER_HOUR * 24 // 17280

/**
 * Convert a time range to an approximate block height range.
 * Returns [startHeight, endHeight] or null for 'all'.
 *
 * The conversion is approximate (±a few blocks) because block time
 * varies slightly. For exact ranges, the blocks table would need a
 * consensus_time index (it doesn't — only created_at is indexed).
 */
export function timeRangeToBlockHeights(
  range: TimeRange,
  latestHeight: number
): { start?: number; end?: number } {
  if (range.preset === 'all') return {}

  if (range.preset === 'custom') {
    const startTime = new Date(range.start + 'T00:00:00Z').getTime() / 1000
    const endTime = new Date(range.end + 'T23:59:59Z').getTime() / 1000
    const now = Date.now() / 1000
    const startBlocksAgo = Math.round((now - startTime) / BLOCK_TIME_SECONDS)
    const endBlocksAgo = Math.round((now - endTime) / BLOCK_TIME_SECONDS)
    return {
      start: latestHeight - startBlocksAgo,
      end: latestHeight - endBlocksAgo,
    }
  }

  const PRESET_DAYS: Record<string, number> = { '1d': 1, '7d': 7, '30d': 30, '90d': 90, '365d': 365 }
  const days = PRESET_DAYS[range.preset] ?? 0
  const blocksAgo = Math.round(days * BLOCKS_PER_DAY)
  return { start: latestHeight - blocksAgo }
}

export function TimeRangeSelector({
  value,
  onChange,
}: {
  value: TimeRange
  onChange: (range: TimeRange) => void
}) {
  const [showCustom, setShowCustom] = useState(value.preset === 'custom')

  useEffect(() => {
    setShowCustom(value.preset === 'custom')
  }, [value])

  const handlePreset = useCallback(
    (preset: string) => {
      if (preset === 'custom') {
        setShowCustom(true)
        const today = new Date().toISOString().slice(0, 10)
        const weekAgo = new Date(Date.now() - 7 * 86400000)
          .toISOString()
          .slice(0, 10)
        onChange({ preset: 'custom', start: weekAgo, end: today })
      } else if (preset === 'all') {
        setShowCustom(false)
        onChange({ preset: 'all' })
      } else {
        setShowCustom(false)
        onChange({ preset: preset as '1d' | '7d' | '30d' | '90d' | '365d' })
      }
    },
    [onChange]
  )

  const activePreset =
    value.preset === 'custom' ? 'custom' : value.preset === 'all' ? 'all' : value.preset

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex rounded-lg border border-border-default bg-surface p-0.5">
        {PRESETS.map((p) => (
          <button
            key={p.value.preset}
            onClick={() => handlePreset(p.value.preset)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              activePreset === p.value.preset
                ? 'bg-accent text-white'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => handlePreset('custom')}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            showCustom
              ? 'bg-accent text-white'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          Custom
        </button>
      </div>

      {showCustom && value.preset === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={value.start}
            onChange={(e) =>
              onChange({ preset: 'custom', start: e.target.value, end: value.end })
            }
            className="rounded-md border border-input-border bg-input-bg px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
          />
          <span className="text-xs text-text-faint">→</span>
          <input
            type="date"
            value={value.end}
            onChange={(e) =>
              onChange({ preset: 'custom', start: value.start, end: e.target.value })
            }
            className="rounded-md border border-input-border bg-input-bg px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
          />
        </div>
      )}
    </div>
  )
}
